import React, { useEffect, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import decisionService from '@/shared/services/decisionService';
import { useTerm } from '@/shared/contexts/TermContext';
import { termLabel } from '@/shared/utils/termLabel';
import { decisionOutcome, programName } from '@/shared/utils/applicationStatus';
import EmptyState from '@/components/common/EmptyState';
import LoadError from '@/components/common/LoadError';
import LoadingState from '@/components/common/LoadingState';
import SortableTh from '@/components/common/SortableTh';
import StatusPill from '@/components/common/StatusPill';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import { SECONDARY_COLUMN } from '@/shared/utils/tableControls';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { useTableParams } from '@/shared/hooks/useTableParams';
import { DECISION_PILL, decisionKey } from '@/shared/utils/statusPills';
import {
    formatDate,
    pillOptions,
} from '@/features/programs/coordinatorColumns';

const LIMIT = 200;
const NOT_RECORDED = 'Not recorded';

const courseLabel = (application) =>
    application.course
        ? `${application.course.subject} ${application.course.courseNumber}`
        : 'Course';

const isNumber = (value) => typeof value === 'number';

const awardedText = (decision) =>
    isNumber(decision.awardedCount) && isNumber(decision.requestedCount)
        ? `${decision.awardedCount} of ${decision.requestedCount}`
        : NOT_RECORDED;

const DECISION_TYPE_ORDER = [
    'approved',
    'partial',
    'contingent',
    'contingent_finalized',
    'contingent_voided',
    'denied',
    'waitlisted',
];

const DECISION_TYPE_OPTIONS = pillOptions(DECISION_PILL, DECISION_TYPE_ORDER);

// Only what the table shows; the decision's notes are never read
const COLUMNS = [
    {
        key: 'course',
        label: 'Course',
        type: 'text',
        value: ({ application }) =>
            application ? courseLabel(application) : null,
        csv: ({ application }) =>
            application
                ? [
                      courseLabel(application),
                      application.courseConfirmation?.sectionNumber &&
                          `Section ${application.courseConfirmation.sectionNumber}`,
                  ]
                      .filter(Boolean)
                      .join(' ')
                : 'Request removed',
    },
    {
        key: 'applicant',
        label: 'Faculty',
        type: 'text',
        value: ({ application }) => application?.applicant?.name,
        csv: ({ application }) => application?.applicant?.name || NOT_RECORDED,
    },
    {
        key: 'decision',
        label: 'Decision',
        type: 'status',
        order: DECISION_TYPE_ORDER,
        value: decisionKey,
        csv: (decision) => decisionOutcome(decision).label,
    },
    {
        key: 'awarded',
        label: 'Awarded',
        type: 'number',
        secondary: true,
        searchable: false,
        value: (decision) => decision.awardedCount,
        csv: awardedText,
    },
    {
        key: 'decided',
        label: 'Decided',
        type: 'date',
        secondary: true,
        value: (decision) => decision.createdAt,
        csv: (decision) => formatDate(decision.createdAt),
    },
    {
        key: 'decidedBy',
        label: 'Decided by',
        type: 'text',
        secondary: true,
        value: (decision) => decision.decidedBy?.name,
        csv: (decision) => decision.decidedBy?.name || NOT_RECORDED,
    },
];

function DecisionRow({ decision }) {
    const { application } = decision;
    return (
        <tr>
            <td>
                {application ? (
                    <>
                        {courseLabel(application)}
                        {application.courseConfirmation?.sectionNumber && (
                            <small className="text-muted d-block">
                                Section{' '}
                                {application.courseConfirmation.sectionNumber}
                            </small>
                        )}
                    </>
                ) : (
                    'Request removed'
                )}
            </td>
            <td>{application?.applicant?.name || NOT_RECORDED}</td>
            <td>
                <StatusPill family="decision" decision={decision} />
            </td>
            <td className={SECONDARY_COLUMN}>{awardedText(decision)}</td>
            <td className={`${SECONDARY_COLUMN} text-nowrap`}>
                {formatDate(decision.createdAt)}
            </td>
            <td className={SECONDARY_COLUMN}>
                {decision.decidedBy?.name || NOT_RECORDED}
            </td>
            <td>
                {application && (
                    <Link
                        to={`/applications/${application._id}`}
                        aria-label={`Open the ${courseLabel(application)} application`}
                    >
                        Open
                    </Link>
                )}
            </td>
        </tr>
    );
}

DecisionRow.propTypes = { decision: PropTypes.object.isRequired };

/**
 * A program's recorded decisions for one term, for its coordinators. The
 * server returns coordinators the program's decisions with their notes; this
 * table reads none of that and shows only the columns below.
 */
function ProgramDecisionsList({ programType, term, termName = null }) {
    const { currentTerm } = useTerm();
    const [state, setState] = useState({ status: 'loading' });
    const [attempt, setAttempt] = useState(0);
    const controls = useTableControls({
        rows: state.decisions || [],
        columns: COLUMNS,
        statusOf: decisionKey,
        persist: useTableParams('decisions'),
    });

    useEffect(() => {
        if (!term) return undefined;
        let cancelled = false;
        setState({ status: 'loading' });
        Promise.resolve()
            .then(() =>
                decisionService.getDecisionsByProgram(programType, {
                    term,
                    limit: LIMIT,
                })
            )
            .then((result) => {
                if (cancelled) return;
                setState({
                    status: 'ready',
                    decisions: result.decisions,
                    total: result.total,
                });
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, [programType, term, attempt]);

    if (!term) {
        return (
            <p className="text-muted mt-3">
                Choose a term to see its decisions.
            </p>
        );
    }
    if (state.status === 'loading') {
        return (
            <div className="mt-3">
                <LoadingState rows={3} label="Loading decisions" />
            </div>
        );
    }
    if (state.status === 'error') {
        return (
            <LoadError
                what="the decisions"
                onRetry={() => setAttempt((n) => n + 1)}
                className="mt-3"
            />
        );
    }
    if (state.decisions.length === 0) {
        return (
            <div className="mt-3">
                <EmptyState message="No decisions recorded for this term yet. Each decision you record on a request appears here." />
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm mt-3">
            <Card.Body className="px-2 px-md-3">
                <div className="mb-3">
                    <TableControlsBar
                        controls={controls}
                        noun="decisions"
                        searchLabel="Search decisions by course, applicant or decider"
                        searchPlaceholder="Search by course, applicant, or decider..."
                        statusLabel="Filter by decision"
                        allLabel="All decisions"
                        statusOptions={DECISION_TYPE_OPTIONS}
                        exportFilename={`${programType}-decisions-${term}.csv`}
                    />
                </div>
                <Table responsive hover className="mb-0">
                    <caption>
                        {programName(programType)} decisions for{' '}
                        {termName || termLabel(currentTerm, term)}
                    </caption>
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <SortableTh
                                    key={column.key}
                                    column={column}
                                    controls={controls}
                                />
                            ))}
                            <th scope="col" className="position-relative">
                                <span className="visually-hidden">Open</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {controls.rows.map((decision) => (
                            <DecisionRow
                                key={decision._id}
                                decision={decision}
                            />
                        ))}
                    </tbody>
                </Table>
                <TableEmpty
                    controls={controls}
                    noun="decisions"
                    emptyText="No decisions recorded for this term yet."
                />
                {state.total > state.decisions.length && (
                    <p className="text-muted small mt-3 mb-0">
                        Showing {state.decisions.length} of {state.total}{' '}
                        decisions.
                    </p>
                )}
            </Card.Body>
        </Card>
    );
}

ProgramDecisionsList.propTypes = {
    programType: PropTypes.string.isRequired,
    term: PropTypes.string,
};

export default ProgramDecisionsList;
